import React, { useState, useEffect, useContext } from 'react';
import { Context } from '../../states/Store';
import useStyles from '../../style/js-style/components/attributes/EACustomColorPickerStyle';
import { MenuItem, Select } from '@mui/material';
import { DefaultSelectProps } from '../../services/common/Constants';
import i18next from 'i18next';
import EAIcon from '../common/EAIcon';
import {
  alphaHexToPercent,
  createHexWithAlpha,
  darkenHex,
  extractHexAndAlpha,
  lightenHex,
} from '../../services/common/Utils';

// Couleurs par défaut
const DEFAULT_COLORS = [
  '#373534',
  '#0059FF',
  '#54CC54',
  '#FF9A00',
  '#FF0001',
  '#F6F9FB',
  '#CCDEFF',
  '#D7F4DC',
  '#FFEBCC',
  '#FFCCCC',
  '#E6E9EB',
  '#99BDFF',
  '#CEF4C3',
  '#FFBB54',
  '#FF999A',
  '#C9CCCF',
  '#377DFF',
  '#60D273',
  '#F4B63E',
  '#FF3F40',
  '#858791',
  '#0047CC',
  '#2D9F40',
  '#CC7B00',
  '#CC0001',
  '#535362',
  '#003699',
  '#32614F',
  '#995C00',
  '#990001',
];

const STORAGE_KEY = 'ea-color-picker-recent-colors';
const MAX_RECENT_COLORS = 10;
const FIXED_COLORS = ['', '#FFFFFF']; // Transparent et blanc (immuables)

const EACustomColorPicker = ({ pickerValue, onChange }) => {
  const { classes } = useStyles();
  const [state] = useContext(Context);

  const [selectedTheme, setSelectedTheme] = useState('default');
  const initialValues = extractHexAndAlpha(pickerValue);
  const [hexValue, setHexValue] = useState(initialValues.color);
  const [previewColor, setPreviewColor] = useState(pickerValue);
  const [opacityPercent, setOpacityPercent] = useState(alphaHexToPercent(initialValues.alpha));
  const previewOpacity = typeof opacityPercent === 'number' ? opacityPercent / 100 : 1;

  // Couleurs utilisées récemment (chargées depuis localStorage)
  const [recentColors, setRecentColors] = useState(DEFAULT_COLORS);

  const themes = () => {
    const colorBackground = (state.personalization.isCustomTemplate && state.personalization.colorBackground) || '#F2F3F7';
    const colorHover = (state.personalization.isCustomTemplate && state.personalization.colorHover) || '#32614F';
    const colorPrimary = (state.personalization.isCustomTemplate && state.personalization.colorPrimary) || '#54CC54';
    const lightenCoefficient = [0.6, 0.4, 0.2, 0.3, 0.7]

    return {
      default: [...DEFAULT_COLORS],
      theme: [
        [colorBackground, colorHover, colorPrimary],
        [
          lightenHex(colorBackground, lightenCoefficient[0]),
          lightenHex(colorHover, lightenCoefficient[0]),
          lightenHex(colorPrimary, lightenCoefficient[0]),
        ],
        [
          lightenHex(colorBackground, lightenCoefficient[1]),
          lightenHex(colorHover, lightenCoefficient[1]),
          lightenHex(colorPrimary, lightenCoefficient[1]),
        ],
        [
          darkenHex(colorBackground, lightenCoefficient[2]),
          darkenHex(colorHover, lightenCoefficient[2]),
          darkenHex(colorPrimary, lightenCoefficient[2]),
        ],
        [
          darkenHex(colorBackground, lightenCoefficient[3]),
          darkenHex(colorHover, lightenCoefficient[3]),
          darkenHex(colorPrimary, lightenCoefficient[3]),
        ],
        [
          darkenHex(colorBackground, lightenCoefficient[4]),
          darkenHex(colorHover, lightenCoefficient[4]),
          darkenHex(colorPrimary, lightenCoefficient[4]),
        ],
      ],
    };
  };

  // Ajouter une couleur à la liste des couleurs récentes
  const addRecentColor = (hexWithAlpha) => {
    // Extraire uniquement la partie RGB sans alpha pour la comparaison et le stockage
    const cleanHex = '#' + extractHexAndAlpha(hexWithAlpha).color;

    // Ne pas ajouter les couleurs fixes à la liste des récentes
    if (FIXED_COLORS.includes(cleanHex)) {
      return;
    }

    // Récupérer les couleurs existantes depuis localStorage
    let savedColors = [];
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        savedColors = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Erreur lors de la lecture du localStorage:', e);
    }

    // Retirer la couleur si elle existe déjà (pour la remettre en première position)
    const filteredColors = savedColors.filter(
      (c) => c.toUpperCase() !== cleanHex.toUpperCase()
    );

    // Ajouter la nouvelle couleur en première position (limité à 8 car 2 cases sont réservées)
    const newColors = [cleanHex, ...filteredColors].slice(0, MAX_RECENT_COLORS - FIXED_COLORS.length);

    // Sauvegarder dans localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newColors));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde dans localStorage:', e);
    }

    // Compléter avec les couleurs par défaut si moins de 8
    const displayColors = [...newColors];
    const maxRecentColors = MAX_RECENT_COLORS - FIXED_COLORS.length;
    if (displayColors.length < maxRecentColors) {
      DEFAULT_COLORS.forEach((color) => {
        if (!displayColors.includes(color) && displayColors.length < maxRecentColors) {
          displayColors.push(color);
        }
      });
    }

    // Ajouter les couleurs fixes au début et mettre à jour l'état
    setRecentColors([...FIXED_COLORS, ...displayColors]);
  };

  const handleSubmit = (color = null) => {
    // Cas spécial pour la couleur transparente
    if (color === '') {
      onChange({
        hex: '',
        source: 'hex',
      });
      return;
    }

    const values = color ? extractHexAndAlpha(color) : null;
    const hexWithAlpha = createHexWithAlpha(
      values?.color ? '#' + values?.color : '#' + hexValue,
      color ? 100 : opacityPercent !== '' ? opacityPercent : 100
    );

    // Ajouter aux couleurs récentes
    addRecentColor(hexWithAlpha);

    onChange({
      hex: hexWithAlpha,
      source: 'hex',
    });
  }

  const handleHexChange = (e) => {
    const value = e.target.value
      .replace(/[^0-9A-Fa-f]/g, '')
      .slice(0, 6)
      .toUpperCase();
    setHexValue(value);
  };

  const handleOpacityChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');

    if (value === '') {
      setOpacityPercent('');
    } else {
      const numValue = Math.max(0, Math.min(100, parseInt(value)));
      setOpacityPercent(numValue);
    }
  };

  useEffect(() => {
    setPreviewColor('#' + hexValue);
  }, [hexValue, opacityPercent]);

  // Si un theme personnalisé existe le choisi en priorité
  useEffect(() => {
    setSelectedTheme(
      state.personalization.isCustomTemplate ? 'theme' : 'default'
    );
  }, [state.personalization]);

  // Extrait la couleur et l'opacité
  useEffect(() => {
    const values = extractHexAndAlpha(pickerValue);

    setHexValue(values.color);
    setOpacityPercent(alphaHexToPercent(values.alpha));
    setPreviewColor(pickerValue);
  }, [pickerValue]);

  // Charger les couleurs récentes depuis localStorage au montage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      let savedColors = [];

      if (stored) {
        savedColors = JSON.parse(stored);
      }

      // Compléter avec les couleurs par défaut si moins de 8 (10 - 2 fixes)
      const displayColors = [...savedColors];
      const maxRecentColors = MAX_RECENT_COLORS - FIXED_COLORS.length;
      if (displayColors.length < maxRecentColors) {
        DEFAULT_COLORS.forEach((color) => {
          if (!displayColors.includes(color) && displayColors.length < maxRecentColors) {
            displayColors.push(color);
          }
        });
      }

      // Ajouter les couleurs fixes au début
      setRecentColors([...FIXED_COLORS, ...displayColors]);
    } catch (e) {
      console.error('Erreur lors du chargement des couleurs récentes:', e);
    }
  }, []);

  return (
    <div className={classes.container}>
      {/* Carré de couleur + Input couleur avec alpha */}
      <div className={classes.colorInputWrapper}>
        {/* Carré de couleur sélectionnée */}
        <div
          className={classes.colorPreview}
          style={{
            backgroundColor: previewColor,
            opacity: previewOpacity,
          }}
        />

        {/* Input hex et opacité */}
        <div className={classes.inputContainer}>
          <span className={classes.inputLabel}>#</span>
          <input
            type="text"
            value={hexValue}
            onChange={handleHexChange}
            onBlur={(e) => {
              handleHexChange(e);
              handleSubmit();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleHexChange(e);
                handleSubmit();
                e.stopPropagation();
                e.preventDefault();
              }
            }}
            className={classes.hexInput}
          />
          <input
            type="text"
            value={opacityPercent}
            onChange={handleOpacityChange}
            onBlur={(e) => {
              handleOpacityChange(e);
              handleSubmit();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleOpacityChange(e);
                handleSubmit();
                e.stopPropagation();
                e.preventDefault();
              }
            }}
            className={classes.opacityInput}
            placeholder="100"
          />
          <span className={classes.inputLabel}>%</span>
        </div>
      </div>

      {/* Couleurs utilisées récemment */}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>
          {i18next.t('calculator.dialog.create.color.recentlyUsedColors')}
        </div>
        <div className={classes.colorGrid}>
          {recentColors.map((color, index) => {
            const currentColor = pickerValue === '' ? '' : extractHexAndAlpha(pickerValue).color;
            const buttonColor = color === '' ? '' : extractHexAndAlpha(color).color;
            const isSelected = (color === '' && pickerValue === '') || (color !== '' && currentColor === buttonColor);
            return (
              <button
                key={index}
                onClick={() => handleSubmit(color)}
                className={`${classes.colorButton} ${
                  isSelected
                    ? color === ''
                      ? classes.emptyColorButtonSelected
                      : classes.colorButtonSelected
                    : classes.colorButtonDefault
                }`}
                style={{
                  backgroundColor: color !== '' && color,
                  position: 'relative',
                }}
              >
                {color === '' && (
                  <EAIcon
                    icon={'colorDot'}
                    style={{ position: 'absolute', top: '-6px', left: '-7px', width: '47px', }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Couleurs du thème avec select */}
      <div>
        <div className={classes.sectionTitleWithSelect}>
          <Select
            value={selectedTheme}
            onChange={(e) => setSelectedTheme(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={classes.themeSelect}
            style={{ backgroundColor: 'white', padding: '0px 5px' }}
            sx={{
              border: 'none',
              fontSize: '12px !important',
              borderRadius: '4px !important',
              width: '100%',
              '& .MuiInput-input': { fontSize: '12px' },
              '& .MuiInputBase-input': { padding: '5px 5px !important' },
              '& .MuiSvgIcon-root': { display: 'none' },
            }}
            MenuProps={{
              ...DefaultSelectProps.MenuProps,
              onClick: (e) => e.stopPropagation(),
              sx: { '& .MuiMenuItem-root': { fontSize: '12px' } },
            }}
          >
            <MenuItem key={'theme'} value={'theme'}>
              <span style={{ fontWeight: 600 }}>
                {i18next.t('calculator.dialog.create.color.themeColors')}
              </span>
            </MenuItem>
            <MenuItem key={'default'} value={'default'}>
              <span style={{ fontWeight: 600 }}>
                {i18next.t('calculator.dialog.create.color.defaultColors')}
              </span>
            </MenuItem>
          </Select>
        </div>
        <div className={selectedTheme === 'theme' ? classes.colorGridTheme : classes.colorGrid}>
          {themes()
            [selectedTheme].flat()
            .map((color, index) => {
              const currentColor = extractHexAndAlpha(pickerValue).color;
              const buttonColor = extractHexAndAlpha(color).color;
              return (
                <button
                  key={index}
                  onClick={() => handleSubmit(color)}
                  className={`${classes.colorButton} ${
                    currentColor === buttonColor
                      ? classes.colorButtonSelected
                      : classes.colorButtonDefault
                  }`}
                  style={{ backgroundColor: color }}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default EACustomColorPicker;